// CoursesTopHeader.js - Courses website themed top header bar component
import { DEVICES } from '../../../constants/breakpoints';

/**
 * Creates a Courses website themed top header bar section
 * @param {function} getHierarchyName - Function to get hierarchy name for elements
 * @param {string} currentPageId - The current page ID where the top header is being added
 * @param {function} setClassStylesRegistry - Function to update class styles registry
 * @param {function} setGlobalClassNames - Function to update global class names
 * @returns {Object} - The top header elements
 */
export const createCoursesTopHeaderElements = (
  getHierarchyName, 
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Define class names
  const topHeaderSectionClass = 'courses-top-header-section';
  const topHeaderContainerClass = 'courses-top-header-container';
  const topHeaderLeftClass = 'courses-top-header-left';
  const topHeaderRightClass = 'courses-top-header-right';
  const topHeaderInfoClass = 'courses-top-header-info';
  const topHeaderInfoItemClass = 'courses-top-header-info-item';
  const topHeaderIconClass = 'courses-top-header-icon';
  const topHeaderTextClass = 'courses-top-header-text';
  const topHeaderSocialClass = 'courses-top-header-social';
  const topHeaderSocialLinkClass = 'courses-top-header-social-link';
  const topHeaderDividerClass = 'courses-top-header-divider';

  // Desktop Styles - Modern courses theme with indigo/purple gradient
  const topHeaderSectionStyles = {
    position: 'relative',
    paddingTop: '8px',
    paddingBottom: '8px',
    paddingLeft: '20px',
    paddingRight: '20px',
    backgroundColor: '#4f46e5',
    background: `linear-gradient(90deg, #4f46e5 0%, #6366f1 50%, #8b5cf6 100%)`,
    color: '#ffffff',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderBottom: '1px solid rgba(139, 92, 246, 0.3)',
    fontSize: '14px',
    fontFamily: '"Inter", sans-serif'
  };

  const topHeaderContainerStyles = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    maxWidth: '1400px',
    width: '100%',
    margin: '0 auto'
  };

  const topHeaderLeftStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: '30px'
  };

  const topHeaderRightStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: '20px'
  };

  const topHeaderInfoStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: '30px'
  };

  const topHeaderInfoItemStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: '#e2e8f0',
    fontSize: '14px',
    fontFamily: '"Inter", sans-serif'
  };

  const topHeaderIconStyles = {
    fontSize: '16px',
    color: '#c7d2fe'
  };

  const topHeaderTextStyles = {
    fontSize: '14px',
    color: '#e2e8f0',
    fontFamily: '"Inter", sans-serif'
  };

  const topHeaderSocialStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  };

  const topHeaderSocialLinkStyles = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    backgroundColor: 'rgba(199, 210, 254, 0.15)',
    border: '1px solid rgba(199, 210, 254, 0.4)',
    color: '#c7d2fe',
    transition: 'all 0.3s ease',
    textDecoration: 'none'
  };

  const topHeaderDividerStyles = {
    width: '1px',
    height: '20px',
    backgroundColor: 'rgba(199, 210, 254, 0.4)',
    margin: '0 10px'
  };

  // Tablet Styles - Hide social icons and divider
  const tabletTopHeaderLeftStyles = {
    ...topHeaderLeftStyles,
    gap: '20px'
  };

  const tabletTopHeaderInfoStyles = {
    ...topHeaderInfoStyles,
    gap: '20px'
  };

  const tabletTopHeaderRightStyles = {
    display: 'none' // Hide entire right section on tablet
  };

  const tabletTopHeaderContainerStyles = {
    ...topHeaderContainerStyles,
    justifyContent: 'center' // Center the content when right section is hidden
  };

  // Mobile Styles - Hide social icons and divider, stack info vertically
  const mobileTopHeaderSectionStyles = {
    ...topHeaderSectionStyles,
    paddingLeft: '15px',
    paddingRight: '15px'
  };

  const mobileTopHeaderContainerStyles = {
    ...topHeaderContainerStyles,
    flexDirection: 'column',
    gap: '10px',
    justifyContent: 'center'
  };

  const mobileTopHeaderLeftStyles = {
    ...topHeaderLeftStyles,
    gap: '15px',
    flexWrap: 'wrap',
    justifyContent: 'center',
    width: '100%'
  };

  const mobileTopHeaderInfoStyles = {
    ...topHeaderInfoStyles,
    gap: '15px',
    flexWrap: 'wrap',
    justifyContent: 'center'
  };

  const mobileTopHeaderInfoItemStyles = {
    ...topHeaderInfoItemStyles,
    fontSize: '13px'
  };

  const mobileTopHeaderTextStyles = {
    ...topHeaderTextStyles,
    fontSize: '13px'
  };

  const mobileTopHeaderRightStyles = {
    display: 'none' // Hide entire right section on mobile
  };

  // Update the classStylesRegistry
  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };
    
    // Desktop styles
    newRegistry[DEVICES.DESKTOP].set(topHeaderSectionClass, topHeaderSectionStyles);
    newRegistry[DEVICES.DESKTOP].set(topHeaderContainerClass, topHeaderContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(topHeaderLeftClass, topHeaderLeftStyles);
    newRegistry[DEVICES.DESKTOP].set(topHeaderRightClass, topHeaderRightStyles);
    newRegistry[DEVICES.DESKTOP].set(topHeaderInfoClass, topHeaderInfoStyles);
    newRegistry[DEVICES.DESKTOP].set(topHeaderInfoItemClass, topHeaderInfoItemStyles);
    newRegistry[DEVICES.DESKTOP].set(topHeaderIconClass, topHeaderIconStyles);
    newRegistry[DEVICES.DESKTOP].set(topHeaderTextClass, topHeaderTextStyles);
    newRegistry[DEVICES.DESKTOP].set(topHeaderSocialClass, topHeaderSocialStyles);
    newRegistry[DEVICES.DESKTOP].set(topHeaderSocialLinkClass, topHeaderSocialLinkStyles);
    newRegistry[DEVICES.DESKTOP].set(topHeaderDividerClass, topHeaderDividerStyles);

    // Tablet styles
    newRegistry[DEVICES.TABLET].set(topHeaderLeftClass, tabletTopHeaderLeftStyles);
    newRegistry[DEVICES.TABLET].set(topHeaderInfoClass, tabletTopHeaderInfoStyles);
    newRegistry[DEVICES.TABLET].set(topHeaderRightClass, tabletTopHeaderRightStyles);
    newRegistry[DEVICES.TABLET].set(topHeaderContainerClass, tabletTopHeaderContainerStyles);

    // Mobile styles
    newRegistry[DEVICES.MOBILE].set(topHeaderSectionClass, mobileTopHeaderSectionStyles);
    newRegistry[DEVICES.MOBILE].set(topHeaderContainerClass, mobileTopHeaderContainerStyles);
    newRegistry[DEVICES.MOBILE].set(topHeaderLeftClass, mobileTopHeaderLeftStyles);
    newRegistry[DEVICES.MOBILE].set(topHeaderInfoClass, mobileTopHeaderInfoStyles);
    newRegistry[DEVICES.MOBILE].set(topHeaderInfoItemClass, mobileTopHeaderInfoItemStyles);
    newRegistry[DEVICES.MOBILE].set(topHeaderTextClass, mobileTopHeaderTextStyles);
    newRegistry[DEVICES.MOBILE].set(topHeaderRightClass, mobileTopHeaderRightStyles);

    return newRegistry;
  });

  // Add classes to globalClassNames
  setGlobalClassNames(prev => new Set([
    ...prev,
    topHeaderSectionClass,
    topHeaderContainerClass,
    topHeaderLeftClass,
    topHeaderRightClass,
    topHeaderInfoClass,
    topHeaderInfoItemClass,
    topHeaderIconClass,
    topHeaderTextClass,
    topHeaderSocialClass,
    topHeaderSocialLinkClass,
    topHeaderDividerClass
  ]));

  // Generate IDs for all elements
  const topHeaderSectionId = Date.now();
  const containerId = topHeaderSectionId + 1;
  const leftId = topHeaderSectionId + 2;
  const rightId = topHeaderSectionId + 3;
  const infoId = topHeaderSectionId + 4;
  const addressItemId = topHeaderSectionId + 5;
  const addressIconId = topHeaderSectionId + 6;
  const addressTextId = topHeaderSectionId + 7;
  const phoneItemId = topHeaderSectionId + 8;
  const phoneIconId = topHeaderSectionId + 9;
  const phoneTextId = topHeaderSectionId + 10;
  const emailItemId = topHeaderSectionId + 11;
  const emailIconId = topHeaderSectionId + 12;
  const emailTextId = topHeaderSectionId + 13;
  const dividerId = topHeaderSectionId + 14;
  const socialId = topHeaderSectionId + 15;
  const facebookId = topHeaderSectionId + 16;
  const facebookIconId = topHeaderSectionId + 17;
  const whatsappId = topHeaderSectionId + 18;
  const whatsappIconId = topHeaderSectionId + 19;
  const tiktokId = topHeaderSectionId + 20;
  const tiktokIconId = topHeaderSectionId + 21;
  const instagramId = topHeaderSectionId + 22;
  const instagramIconId = topHeaderSectionId + 23;

  const newElements = [
    // Main Top Header Section Container
    {
      id: topHeaderSectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'section',
      name: 'Courses Top Header Section',
      parentId: null,
      className: topHeaderSectionClass,
      styles: {},
      children: [containerId],
      text: '',
      attributes: {
        'data-element': 'courses-top-header-section',
        'data-component': 'section',
        'data-section-type': 'top-header',
        'data-page-id': currentPageId
      }
    },
    
    // Container
    {
      id: containerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Courses Top Header Container',
      parentId: topHeaderSectionId,
      hierarchyName: getHierarchyName('div'),
      className: topHeaderContainerClass,
      styles: {},
      children: [leftId, rightId],
      text: '',
      attributes: {
        'data-element': 'courses-top-header-container'
      }
    },
    
    // Left Section
    {
      id: leftId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Courses Top Header Left',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: topHeaderLeftClass,
      styles: {},
      children: [infoId],
      text: '',
      attributes: {
        'data-element': 'courses-top-header-left'
      }
    },
    
    // Company Info Container
    {
      id: infoId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Courses Top Header Info',
      parentId: leftId,
      hierarchyName: getHierarchyName('div'),
      className: topHeaderInfoClass,
      styles: {},
      children: [addressItemId, phoneItemId, emailItemId],
      text: '',
      attributes: {
        'data-element': 'courses-top-header-info'
      }
    },
    
    // Address Item
    {
      id: addressItemId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Courses Top Header Address',
      parentId: infoId,
      hierarchyName: getHierarchyName('div'),
      className: topHeaderInfoItemClass,
      styles: {},
      children: [addressIconId, addressTextId],
      text: '',
      attributes: {
        'data-element': 'courses-top-header-info-item'
      }
    },
    
    // Address Icon
    {
      id: addressIconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Address Icon',
      parentId: addressItemId,
      hierarchyName: getHierarchyName('image'),
      className: topHeaderIconClass,
      styles: {
        width: '16px',
        height: '16px'
      },
      src: 'https://api.iconify.design/mdi:map-marker.svg?color=%23c7d2fe',
      children: [],
      text: '',
      attributes: {
        'data-element': 'courses-top-header-icon',
        'alt': 'Address'
      }
    },
    
    // Address Text
    {
      id: addressTextId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'span',
      name: 'Address Text',
      parentId: addressItemId,
      hierarchyName: getHierarchyName('span'),
      className: topHeaderTextClass,
      styles: {},
      children: [],
      text: 'Chak#: 102 JB Burj Village Faisalabad, Pakistan',
      attributes: {
        'data-element': 'courses-top-header-text'
      }
    },
    
    // Phone Item
    {
      id: phoneItemId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Courses Top Header Phone',
      parentId: infoId,
      hierarchyName: getHierarchyName('div'),
      className: topHeaderInfoItemClass,
      styles: {},
      children: [phoneIconId, phoneTextId],
      text: '',
      attributes: {
        'data-element': 'courses-top-header-info-item'
      }
    },
    
    // Phone Icon
    {
      id: phoneIconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Phone Icon',
      parentId: phoneItemId,
      hierarchyName: getHierarchyName('image'),
      className: topHeaderIconClass,
      styles: {
        width: '16px',
        height: '16px'
      },
      src: 'https://api.iconify.design/mdi:phone.svg?color=%23c7d2fe',
      children: [],
      text: '',
      attributes: {
        'data-element': 'courses-top-header-icon',
        'alt': 'Phone'
      }
    },
    
    // Phone Text
    {
      id: phoneTextId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'span',
      name: 'Phone Text',
      parentId: phoneItemId,
      hierarchyName: getHierarchyName('span'),
      className: topHeaderTextClass,
      styles: {},
      children: [],
      text: '03012491171',
      attributes: {
        'data-element': 'courses-top-header-text'
      }
    },
    
    // Email Item
    {
      id: emailItemId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Courses Top Header Email',
      parentId: infoId,
      hierarchyName: getHierarchyName('div'),
      className: topHeaderInfoItemClass,
      styles: {},
      children: [emailIconId, emailTextId],
      text: '',
      attributes: {
        'data-element': 'courses-top-header-info-item'
      }
    },
    
    // Email Icon
    {
      id: emailIconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Email Icon',
      parentId: emailItemId,
      hierarchyName: getHierarchyName('image'),
      className: topHeaderIconClass,
      styles: {
        width: '16px',
        height: '16px'
      },
      src: 'https://api.iconify.design/mdi:email.svg?color=%23c7d2fe',
      children: [],
      text: '',
      attributes: {
        'data-element': 'courses-top-header-icon',
        'alt': 'Email'
      }
    },
    
    // Email Text
    {
      id: emailTextId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'span',
      name: 'Email Text',
      parentId: emailItemId,
      hierarchyName: getHierarchyName('span'),
      className: topHeaderTextClass,
      styles: {},
      children: [],
      text: 'snit.org@hotmail.com',
      attributes: {
        'data-element': 'courses-top-header-text'
      }
    },
    
    // Right Section
    {
      id: rightId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Courses Top Header Right',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: topHeaderRightClass,
      styles: {},
      children: [dividerId, socialId],
      text: '',
      attributes: {
        'data-element': 'courses-top-header-right'
      }
    },
    
    // Divider
    {
      id: dividerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Courses Top Header Divider',
      parentId: rightId,
      hierarchyName: getHierarchyName('div'),
      className: topHeaderDividerClass,
      styles: {},
      children: [],
      text: '',
      attributes: {
        'data-element': 'courses-top-header-divider'
      }
    },
    
    // Social Links Container
    {
      id: socialId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Courses Top Header Social',
      parentId: rightId,
      hierarchyName: getHierarchyName('div'),
      className: topHeaderSocialClass,
      styles: {},
      children: [facebookId, whatsappId, tiktokId, instagramId],
      text: '',
      attributes: {
        'data-element': 'courses-top-header-social'
      }
    },
    
    // Facebook Link
    {
      id: facebookId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Courses Top Header Facebook',
      parentId: socialId,
      hierarchyName: getHierarchyName('link'),
      className: topHeaderSocialLinkClass,
      styles: {},
      children: [facebookIconId],
      text: '',
      linkTo: 'https://www.facebook.com/share/16Q7ppag35/',
      attributes: {
        'data-element': 'courses-top-header-social-link',
        'aria-label': 'Facebook'
      }
    },
    
    // Facebook Icon
    {
      id: facebookIconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Facebook Icon',
      parentId: facebookId,
      hierarchyName: getHierarchyName('image'),
      styles: {
        width: '14px',
        height: '14px'
      },
      src: 'https://api.iconify.design/mdi:facebook.svg?color=%23c7d2fe',
      children: [],
      text: '',
      attributes: {
        'data-element': 'social-icon-img',
        'alt': 'Facebook'
      }
    },
    
    // WhatsApp Link
    {
      id: whatsappId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Courses Top Header WhatsApp',
      parentId: socialId,
      hierarchyName: getHierarchyName('link'),
      className: topHeaderSocialLinkClass,
      styles: {},
      children: [whatsappIconId],
      text: '',
      linkTo: 'https://whatsapp.com/channel/0029Vb4nVx57oQhazj6xAt41',
      attributes: {
        'data-element': 'courses-top-header-social-link',
        'aria-label': 'WhatsApp'
      }
    },
    
    // WhatsApp Icon
    {
      id: whatsappIconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'WhatsApp Icon',
      parentId: whatsappId,
      hierarchyName: getHierarchyName('image'),
      styles: {
        width: '14px',
        height: '14px'
      },
      src: 'https://api.iconify.design/mdi:whatsapp.svg?color=%23c7d2fe',
      children: [],
      text: '',
      attributes: {
        'data-element': 'social-icon-img',
        'alt': 'WhatsApp'
      }
    },
    
    // TikTok Link
    {
      id: tiktokId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Courses Top Header TikTok',
      parentId: socialId,
      hierarchyName: getHierarchyName('link'),
      className: topHeaderSocialLinkClass,
      styles: {},
      children: [tiktokIconId],
      text: '',
      linkTo: 'https://www.tiktok.com/@snitfaisalabad?_t=ZS-8wjL0zMp8HR&_r=1https://www.tiktok.com/@snitfaisalabad?_t=ZS-8wjL0zMp8HR&_r=1',
      attributes: {
        'data-element': 'courses-top-header-social-link',
        'href': '#',
        'aria-label': 'TikTok'
      }
    },
    
    // TikTok Icon
    {
      id: tiktokIconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'TikTok Icon',
      parentId: tiktokId,
      hierarchyName: getHierarchyName('image'),
      styles: {
        width: '14px',
        height: '14px'
      },
      src: 'https://api.iconify.design/simple-icons:tiktok.svg?color=%23c7d2fe',
      children: [],
      text: '',
      attributes: {
        'data-element': 'social-icon-img',
        'alt': 'TikTok'
      }
    },
    
    // Instagram Link
    {
      id: instagramId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Courses Top Header Instagram',
      parentId: socialId,
      hierarchyName: getHierarchyName('link'),
      className: topHeaderSocialLinkClass,
      styles: {},
      children: [instagramIconId],
      text: '',
      linkTo: 'https://www.instagram.com/snitfaisalabad?igsh=MW5hMXNnNnJtZjg1bw==',
      attributes: {
        'data-element': 'courses-top-header-social-link',
        'aria-label': 'Instagram'
      }
    },
    
    // Instagram Icon
    {
      id: instagramIconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Instagram Icon',
      parentId: instagramId,
      hierarchyName: getHierarchyName('image'),
      styles: {
        width: '14px',
        height: '14px'
      },
      src: 'https://api.iconify.design/mdi:instagram.svg?color=%23c7d2fe',
      children: [],
      text: '',
      attributes: {
        'data-element': 'social-icon-img',
        'alt': 'Instagram'
      }
    }
  ];
  
  // Return all elements
  return {
    coursesTopHeaderSection: newElements[0],
    elements: newElements
  };
};

export default createCoursesTopHeaderElements; 